'use client';

// npm library imports
import { ReactNode, useCallback } from 'react';

// embla carousel imports
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// style imports
import emblaStyles from './embla.module.scss';

// type imports
import { CarouselImage } from '@lib/types';

// local component imports
import CarouselDots from './_CarouselDots';
import CarouselArrows from './_CarouselArrows';
import _SingleImageCarousel from './_SingleImageCarousel';

/**
 * Carousel Cabin Content component that displays images and content for a section.
 * 
 * @remarks 
 * This component is really only used for the Cabin section of the homepage, but could be used elsewhere.
 * I couldn't find a better way to pass in the images. We also decided that it would be best to keep
 * the navigation dots and arrows within thhe images as otherwise they kinda get lost and dont fit well on the page.
 * 
 * @param props - The component props object
 * @param props.children - Child components to render. Each child will be displayed in a separate slide.
 * @param props.images - The array of CarouselImages. Each image will be displayed in a separate slide.
 * @param props.overlap Optional - Used to position the carousel overlapping other sections
 * @returns Carousel Cabin Slider with all the content and images
 * 
 * @example
 * ```tsx
 * <CarouselCabinContent
 *  overlap="top"
 *  images={[
 *      cab1.carouselImages[0],
 *      cab2.carouselImages[0],
 *      cab3.carouselImages[0],
 *  ]}
 * >
 *  <Container margin="both">
 *      <Heading content={cab1.heading} />
 *      <Text content={cab1.text} />
 *      <LinkComponent link1={cab1.link1} link2={cab1.link2} />
 *  </Container>
 *  <Container margin="both">
 *      <Heading content={cab2.heading} />
 *      <Text content={cab2.text} />
 *      <LinkComponent link1={cab2.link1} link2={cab2.link2} />
 *  </Container>
 * </CarouselCabinContent>
 * ```
 *  
 */
const CarouselCabinContent = ({
    children,
    images,
    overlap = 'none',
}: {
    children: ReactNode[]; // Array of content
    images: CarouselImage[]; // Array of CarouselImage objects
    overlap?: 'none' | 'top' | 'bottom'; //adjust margin to overlap sections
}) => {
    // Setup the embla carousel Ref and API with options and autoplay plugin
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop: true, skipSnaps: true },
        [Autoplay({ delay: 12000, stopOnInteraction: true })],
    );

    // Function to handle autoplay on navigation button clicks.
    // required for arrows and dots to function.
    const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;

        const resetOrStop =
            autoplay.options.stopOnInteraction === false
                ? autoplay.reset
                : autoplay.stop;

        resetOrStop();
    }, []);

    return (
        <>
            {/* Carousel container  with overlap style and dynamic max width */}
            <div className={`${emblaStyles.embla} ${emblaStyles[overlap]}`}>
                <div className={emblaStyles.embla__viewport} ref={emblaRef}>
                    <div className={emblaStyles.embla__container}>
                        {/* Map through child elements to create carousel slides */}
                        {children.map((content, index) => (
                            /* Whole section Carousel Slide with Extra class for image and content to center correctly */
                            <div
                                className={`${emblaStyles.embla__slide} ${emblaStyles.contentflex}`}
                                key={index}
                            >
                                {/* Mini Carousel inside the larger one for rendering buttons and arrows better */}
                                <div
                                    className={`${emblaStyles.embla}`}
                                    style={{
                                        width: '100%',
                                        maxWidth: `${images[index].width}px`,
                                    }}
                                >
                                    <div
                                        className={emblaStyles.embla__viewport}
                                    >
                                        <div
                                            className={
                                                emblaStyles.embla__container
                                            }
                                        >
                                            <div                                                
                                                className={`${emblaStyles.embla__slide} `}
                                            >
                                                {/* Carousel Image component */}
                                                <_SingleImageCarousel
                                                    image={images[index]}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={emblaStyles.embla__controls}>
                                        <CarouselArrows
                                            emblaApi={emblaApi}
                                            onNavButtonClick={onNavButtonClick}
                                        />
                                        <CarouselDots
                                            emblaApi={emblaApi}
                                            onNavButtonClick={onNavButtonClick}
                                        />
                                    </div>
                                </div>
                                {/* Children Content to be displayed in the carousel slide */}
                                {content}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CarouselCabinContent;
